import { i18n } from '@/lib/i18n'
import type { ContactLink } from '@/lib/widget-contacts'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, OAUTH_START_CONFIG } from '@/lib/api-client'

// ─────────────────────────────────────────────────────────────────────────────
// Kiểu dữ liệu
// ─────────────────────────────────────────────────────────────────────────────

/** Mã nền tảng (khớp backend `shared/constants.ts`). */
export const PLATFORM = {
  ZALO_OA: 1,
  ZALO_USER: 2,
  ZALO_BOT: 3,
  FACEBOOK_PAGE: 10,
  TELEGRAM: 12,
  TELEGRAM_BUSINESS: 13,
  TIKTOK_SHOP: 40,
  SHOPEE: 41,
} as const

/** Trạng thái sống của một kênh (liveStatus từ backend). */
export type ChannelStatus =
  | 'connected'
  | 'connecting'
  | 'qr_pending'
  | 'webhook_pending'
  | 'token_expired'
  | 'disconnected'
  /** Zalo cá nhân bị phiên ở nơi khác chiếm (Smax, Zalo PC, chat.zalo.me). */
  | 'kicked'
  | (string & {})

export interface ChannelOwner {
  id: string
  fullName: string
  email: string
}

/** Bản ghi tài khoản kênh trả về từ `/zalo-accounts`. */
export interface ChannelAccount {
  id: string
  platform: number
  externalUid: string | null
  externalPageId: string | null
  displayName: string | null
  avatarUrl: string | null
  phone: string | null
  status: ChannelStatus | null
  isDisabled: boolean
  /** Tắt bởi quản trị hệ thống — tổ chức không tự bật lại (C6). */
  lockedByPlatform?: boolean
  lastConnectedAt: string | null
  createdAt: string
  owner: ChannelOwner | null
  liveStatus: ChannelStatus
  /** Mức của người dùng trên kênh: 'manage' = Quản lý kênh (đủ thông tin); 'agent' = chỉ Tiếp nhận (bản rút gọn, chỉ có từ `useInboxAccounts`). */
  accessLevel?: 'manage' | 'agent'
  /** Có khi liveStatus = 'kicked': lúc bị đá và mã Zalo trả về (3000/3003). */
  kickedAt?: string
  kickCode?: number
}

export interface PancakePage {
  id: string
  name: string
  platform: string
  platformLabel: string
  shopId: string | null
  isActive: boolean
  isConnected: boolean
  hasToken: boolean
  activeUsers: number
}

export interface PancakeAccount {
  id: string
  displayName: string | null
  platform: number
  platformLabel: string
  externalPageId: string | null
  status: ChannelStatus | null
  lastConnectedAt: string | null
  createdAt: string
}

// ─────────────────────────────────────────────────────────────────────────────
// Zalo — tài khoản (cá nhân + OA dùng chung endpoint, lọc theo ?type)
// ─────────────────────────────────────────────────────────────────────────────

export const zaloAccountsKey = (type?: 'personal' | 'oa', connectedOnly?: boolean) =>
  ['integrations', 'zalo-accounts', type ?? 'all', connectedOnly ? 'connected' : 'all-status'] as const

/**
 * Danh sách tài khoản kênh.
 * - `type='personal'` → Zalo cá nhân (platform 2)
 * - `type='oa'`       → Zalo OA (platform 1)
 * - bỏ trống          → tất cả kênh (dùng để lọc Facebook phía client)
 */
export function useZaloAccounts(type?: 'personal' | 'oa', connectedOnly = false, enabled = true) {
  return useQuery<ChannelAccount[]>({
    queryKey: zaloAccountsKey(type, connectedOnly),
    enabled,
    // Lưới an toàn khi lỡ sự kiện socket `zalo:account-status` (mất mạng, máy ngủ…):
    // trạng thái kết nối không bao giờ cũ quá một phút, và làm mới khi quay lại tab.
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    queryFn: async () => {
      const { data } = await api.get<ChannelAccount[]>('/zalo-accounts', {
        params: {
          ...(type ? { type } : {}),
          // Chỉ tài khoản đã kết nối thật. Trang Tích hợp KHÔNG bật cờ này vì ở đó
          // cần thấy cả bản nháp `qr_pending` để quét QR tiếp.
          ...(connectedOnly ? { connected: 'true' } : {}),
        },
      })
      return data
    },
  })
}

/**
 * Danh sách kênh cho HỘP THƯ (bộ chọn tài khoản, bộ lọc, trạng thái kênh gửi): gồm cả kênh người dùng chỉ có mức
 * "Tiếp nhận" (bản rút gọn — không có chủ nick / SĐT / UID). Các trang quản lý (Tích hợp, Đội ngũ…) vẫn dùng
 * `useZaloAccounts` — chỉ kênh mình quản lý.
 */
export function useInboxAccounts() {
  return useQuery<ChannelAccount[]>({
    // Tiền tố ['integrations','zalo-accounts'] — sự kiện socket / mutation làm mới chung cùng nhóm.
    queryKey: [...zaloAccountsKey(undefined, false), 'inbox'],
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    queryFn: async () => (await api.get<ChannelAccount[]>('/zalo-accounts', { params: { forInbox: 'true' } })).data,
  })
}

/** Bắt đầu kết nối Zalo cá nhân qua QR (tạo account tạm + phát sự kiện `zalo:qr`). */
export function useConnectZalo() {
  const qc = useQueryClient()
  return useMutation<{ accountId: string; message: string }, unknown, { phone?: string } | void>({
    mutationFn: async (body) => {
      const { data } = await api.post<{ accountId: string; message: string }>(
        '/zalo-accounts/connect',
        body ?? {},
      )
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['integrations', 'zalo-accounts'] }),
  })
}

/** Đăng nhập lại bằng QR cho một account đã tồn tại. */
export function useZaloLogin() {
  return useMutation<{ accountId: string; message: string }, unknown, string>({
    mutationFn: async (id) => {
      const { data } = await api.post<{ accountId: string; message: string }>(
        `/zalo-accounts/${id}/login`,
      )
      return data
    },
  })
}

/** Kết nối lại bằng phiên đã lưu (không cần quét QR). */
export function useZaloReconnect() {
  const qc = useQueryClient()
  return useMutation<{ accountId: string; message: string }, unknown, string>({
    mutationFn: async (id) => {
      const { data } = await api.post<{ accountId: string; message: string }>(
        `/zalo-accounts/${id}/reconnect`,
      )
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['integrations', 'zalo-accounts'] }),
  })
}

/** Ngắt kết nối / xóa mềm một tài khoản kênh. */
/** Kết quả một lượt đồng bộ danh bạ (bạn bè + nhóm) của một tài khoản Zalo. */
export interface DirectorySyncResult {
  friends: number
  friendPages: number
  /** Chạm trần số trang — danh sách bạn có thể còn sót. */
  truncated: boolean
  groups: number
  members: number
  message: string
}

/**
 * Kéo lại toàn bộ bạn bè + nhóm của một tài khoản Zalo.
 *
 * Chạy đồng bộ (không hàng đợi) nên có thể mất vài chục giây với tài khoản
 * nhiều bạn — nút gọi nó phải hiện trạng thái đang chạy.
 */
export function useSyncZaloDirectory() {
  const qc = useQueryClient()
  return useMutation<DirectorySyncResult, unknown, string>({
    mutationFn: async (id) => {
      const { data } = await api.post<DirectorySyncResult>(`/zalo-accounts/${id}/directory/sync`)
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['integrations', 'zalo-accounts'] })
      // Danh bạ vừa đổi → danh sách hội thoại và khách hàng có thể hiện tên/ảnh mới.
      qc.invalidateQueries({ queryKey: ['conversations'] })
      qc.invalidateQueries({ queryKey: ['contacts'] })
    },
  })
}

export function useDeleteZaloAccount() {
  const qc = useQueryClient()
  return useMutation<void, unknown, string>({
    mutationFn: async (id) => {
      await api.delete(`/zalo-accounts/${id}`)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['integrations', 'zalo-accounts'] }),
  })
}

// ─────────────────────────────────────────────────────────────────────────────
// OAuth (Zalo OA + Facebook Page) — lấy URL rồi redirect
// ─────────────────────────────────────────────────────────────────────────────

/** Lấy URL uỷ quyền OAuth (backend trả `{ url }`). */
/**
 * `returnTo` (chỉ `/onboarding` — backend lọc theo danh sách cho phép): callback OAuth đưa về trang này thay vì
 * trang Tích hợp. Được lưu trong payload `state` phía server.
 */
export interface OAuthStartOptions {
  returnTo?: string
}

async function fetchOAuthUrl(path: string, opts?: OAuthStartOptions | void): Promise<string> {
  const { data } = await api.get<{ url: string }>(path, { ...OAUTH_START_CONFIG, ...(opts?.returnTo ? { params: { returnTo: opts.returnTo } } : {}) })
  return data.url
}

/** Bắt đầu kết nối Zalo OA (redirect sang trang uỷ quyền). */
export function useConnectZaloOa() {
  return useMutation<string, unknown, OAuthStartOptions | void>({
    mutationFn: (opts) => fetchOAuthUrl('/zalo-oa/connect/start', opts),
  })
}

/** Bắt đầu kết nối Facebook Page (redirect sang hộp thoại đăng nhập Facebook). */
export function useConnectFacebookPage() {
  return useMutation<string, unknown, OAuthStartOptions | void>({
    mutationFn: (opts) => fetchOAuthUrl('/facebook-page/connect/start', opts),
  })
}

// ─────────────────────────────────────────────────────────────────────────────
// TikTok Shop — cấu hình khoá ứng dụng theo org + OAuth
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Cấu hình TikTok Shop của org. Khác Zalo OA (đọc từ biến môi trường): App Key /
 * App Secret lưu theo org qua `app_settings`, nên tab TikTok có form nhập trực tiếp.
 */
export interface TiktokConfig {
  appKey: string
  /** vbsec H29: server chỉ trả có/chưa cấu hình, không còn trả 4+4 ký tự đầu/cuối secret. */
  hasAppSecret: boolean
  redirectUri: string
  webhookUrl: string
  isConfigured: boolean
}

/** `enabled` = false khi người xem thiếu `integrations.update` (GET /tiktok-shop/config trả 403) — khỏi gọi vô ích. */
export function useTiktokConfig(enabled = true) {
  return useQuery<TiktokConfig>({
    queryKey: ['integrations', 'tiktok-shop', 'config'],
    enabled,
    queryFn: async () => {
      const { data } = await api.get<TiktokConfig>('/tiktok-shop/config')
      return data
    },
  })
}

/** Lưu App Key / App Secret của TikTok Shop. Bỏ trống appSecret nếu chỉ đổi App Key. */
export function useSaveTiktokConfig() {
  const qc = useQueryClient()
  return useMutation<
    { success: boolean; message: string },
    unknown,
    { appKey: string; appSecret?: string }
  >({
    mutationFn: async (body) => {
      const { data } = await api.post<{ success: boolean; message: string }>(
        '/tiktok-shop/config',
        body,
      )
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['integrations', 'tiktok-shop', 'config'] }),
  })
}

/** Kết quả chẩn đoán chat người mua (Customer Service API) của một gian hàng. */
export interface TiktokChatDiagnosis {
  status: 'ok' | 'scope_missing' | 'token_expired' | 'not_connected' | 'no_creds' | 'bad_credentials' | 'error'
  message: string
  errorCode?: number
  hasConversations?: boolean
  accountId: string
  shopName: string | null
  /** Số hội thoại TikTok đã về eCDP (qua webhook). */
  soHoiThoai: number
  tinGanNhat: string | null
}

export function useDiagnoseTiktokChat() {
  return useMutation<TiktokChatDiagnosis, unknown, string>({
    mutationFn: async (id) => {
      const { data } = await api.get<TiktokChatDiagnosis>(`/tiktok-shop/${id}/diagnose-chat`)
      return data
    },
  })
}

/**
 * Bắt đầu kết nối TikTok Shop → trả URL uỷ quyền để redirect.
 * Lưu ý: endpoint này trả `{ authUrl }` (không phải `{ url }` như Zalo/Facebook).
 */
export function useConnectTiktok() {
  return useMutation<string, unknown, OAuthStartOptions | void>({
    mutationFn: async (opts) => {
      const { data } = await api.post<{ authUrl: string }>(
        '/tiktok-shop/connect/start',
        opts?.returnTo ? { returnTo: opts.returnTo } : {},
        OAUTH_START_CONFIG,
      )
      return data.authUrl
    },
  })
}

/**
 * Các gian hàng TikTok Shop đã kết nối. Dùng chung endpoint `/zalo-accounts`
 * (trả mọi kênh của org khi không lọc `type`) rồi lọc theo platform TIKTOK_SHOP.
 */
export function useTiktokAccounts() {
  return useQuery<ChannelAccount[]>({
    queryKey: [...zaloAccountsKey(undefined, false), 'tiktok'],
    queryFn: async () => {
      const { data } = await api.get<ChannelAccount[]>('/zalo-accounts')
      return data.filter((a) => a.platform === PLATFORM.TIKTOK_SHOP)
    },
  })
}

// ─────────────────────────────────────────────────────────────────────────────
// Shopee — cấu hình app theo org + OAuth uỷ quyền shop (chat chờ whitelist)
// ─────────────────────────────────────────────────────────────────────────────

export type ShopeeEnv = 'live' | 'test'

/** Cấu hình Shopee của org. Partner Key mã hoá phía máy chủ, chỉ trả bản đã che. */
export interface ShopeeConfig {
  partnerId: string
  partnerKeyMasked: string
  hasPartnerKey: boolean
  /** Push Partner Key riêng (Live) — Shopee ký Push bằng khoá này, khác Partner Key gọi API. */
  pushPartnerKeyMasked: string
  hasPushPartnerKey: boolean
  env: ShopeeEnv
  host: string
  redirectUri: string
  pushUrl: string
  isConfigured: boolean
}

const SHOPEE_CONFIG_KEY = ['integrations', 'shopee', 'config'] as const

export function useShopeeConfig() {
  return useQuery<ShopeeConfig>({
    queryKey: SHOPEE_CONFIG_KEY,
    queryFn: async () => {
      const { data } = await api.get<ShopeeConfig>('/shopee/config')
      return data
    },
  })
}

/** Lưu Partner ID / môi trường; bỏ trống partnerKey / pushPartnerKey nếu không đổi khoá. */
export function useSaveShopeeConfig() {
  const qc = useQueryClient()
  return useMutation<
    { success: boolean; message: string },
    unknown,
    { partnerId: string; partnerKey?: string; pushPartnerKey?: string; env: ShopeeEnv }
  >({
    mutationFn: async (body) => {
      const { data } = await api.post<{ success: boolean; message: string }>('/shopee/config', body)
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: SHOPEE_CONFIG_KEY }),
  })
}

/** Bắt đầu uỷ quyền shop Shopee → trả `authUrl` để redirect (giống TikTok Shop). */
export function useConnectShopee() {
  return useMutation<string, unknown, OAuthStartOptions | void>({
    mutationFn: async (opts) => {
      const { data } = await api.post<{ authUrl: string }>(
        '/shopee/connect/start',
        opts?.returnTo ? { returnTo: opts.returnTo } : {},
        OAUTH_START_CONFIG,
      )
      return data.authUrl
    },
  })
}

/** Các shop Shopee đã kết nối — lọc từ `/zalo-accounts` như TikTok Shop. */
export function useShopeeAccounts() {
  return useQuery<ChannelAccount[]>({
    queryKey: [...zaloAccountsKey(undefined, false), 'shopee'],
    queryFn: async () => {
      const { data } = await api.get<ChannelAccount[]>('/zalo-accounts')
      return data.filter((a) => a.platform === PLATFORM.SHOPEE)
    },
  })
}

// ─────────────────────────────────────────────────────────────────────────────
// Pancake
// ─────────────────────────────────────────────────────────────────────────────

export function usePancakeConfig() {
  return useQuery<{ hasToken: boolean }>({
    queryKey: ['integrations', 'pancake', 'config'],
    queryFn: async () => {
      const { data } = await api.get<{ hasToken: boolean }>('/pancake/config')
      return data
    },
  })
}

/** Lưu user access token của Pancake (backend tự xác thực bằng cách gọi listPages). */
export function useSavePancakeToken() {
  const qc = useQueryClient()
  return useMutation<{ ok: boolean }, unknown, string>({
    mutationFn: async (userAccessToken) => {
      const { data } = await api.post<{ ok: boolean }>('/pancake/config', { userAccessToken })
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['integrations', 'pancake'] }),
  })
}

/** Danh sách page khả dụng từ Pancake (chỉ gọi khi đã có token). */
export function usePancakePages(enabled: boolean) {
  return useQuery<PancakePage[]>({
    queryKey: ['integrations', 'pancake', 'pages'],
    enabled,
    queryFn: async () => {
      const { data } = await api.get<{ pages: PancakePage[] }>('/pancake/pages')
      return data.pages
    },
  })
}

/** Các tài khoản Pancake đã kết nối. */
export function usePancakeConnected() {
  return useQuery<PancakeAccount[]>({
    queryKey: ['integrations', 'pancake', 'connected'],
    queryFn: async () => {
      const { data } = await api.get<{ accounts: PancakeAccount[] }>('/pancake/connected')
      return data.accounts
    },
  })
}

export function useConnectPancakePage() {
  const qc = useQueryClient()
  return useMutation<
    { ok: boolean; channelAccountId: string },
    unknown,
    { pageId: string; pageName: string; platform: string }
  >({
    mutationFn: async (body) => {
      const { data } = await api.post<{ ok: boolean; channelAccountId: string }>(
        '/pancake/connect',
        body,
      )
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['integrations', 'pancake'] }),
  })
}

export function useDisconnectPancakePage() {
  const qc = useQueryClient()
  return useMutation<{ ok: boolean }, unknown, string>({
    mutationFn: async (id) => {
      const { data } = await api.delete<{ ok: boolean }>(`/pancake/disconnect/${id}`)
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['integrations', 'pancake'] }),
  })
}

export function useSyncPancakePage() {
  const qc = useQueryClient()
  return useMutation<{ ok: boolean; message: string }, unknown, string>({
    mutationFn: async (id) => {
      const { data } = await api.post<{ ok: boolean; message: string }>(`/pancake/sync/${id}`, {
        direction: 'pull',
      })
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['integrations', 'pancake'] }),
  })
}

// ─────────────────────────────────────────────────────────────────────────────
// Tiện ích hiển thị
// ─────────────────────────────────────────────────────────────────────────────

// Nhãn trạng thái nằm ở lib/account-status (thuần, có test) — giữ đường import cũ.
export { statusMeta, type StatusMeta } from '@/lib/account-status'

/** Trạng thái cấu hình Zalo OA — cho biết thiếu biến môi trường nào. */
export interface OaConfigStatus {
  configured: boolean
  missing: string[]
  redirectUri: string
  /** Redirect URI trỏ tên miền khác API đang chạy — thường là sót sau khi đổi domain. */
  hostMismatch: boolean
  /** Gốc API mà backend tự nhận (PUBLIC_API_URL). */
  apiBase: string
  webhookUrl: string
  webhookStrict: boolean
  /** Số sự kiện webhook bị bỏ vì chữ ký không khớp, tính từ lúc backend khởi động. */
  unverifiedWebhookEvents: number
}

export function useZaloOaConfig() {
  return useQuery<OaConfigStatus>({
    queryKey: ['integrations', 'zalo-oa', 'config'],
    queryFn: async () => {
      const { data } = await api.get<OaConfigStatus>('/zalo-oa/config-status')
      return data
    },
  })
}

/** Trạng thái cấu hình Facebook Page. */
export interface FbConfigStatus {
  configured: boolean
  missing: string[]
  redirectUri: string
  /** Redirect URI trỏ tên miền khác API đang chạy — thường là sót sau khi đổi domain. */
  hostMismatch: boolean
  /** Gốc API mà backend tự nhận (PUBLIC_API_URL). */
  apiBase: string
  webhookUrl: string
  graphVersion: string
  scopes: string[]
  webhookFields: string[]
  /** Chuỗi gợi ý cho verify token — chỉ có khi chưa cấu hình. */
  suggestedVerifyToken: string | null
}

export function useFacebookConfig() {
  return useQuery<FbConfigStatus>({
    queryKey: ['integrations', 'facebook-page', 'config'],
    queryFn: async () => {
      const { data } = await api.get<FbConfigStatus>('/facebook-page/config-status')
      return data
    },
  })
}

// ── Kho lưu trữ module ──────────────────────────────────────────────

export interface AppModule {
  key: string
  label: string
  archived: boolean
  reason?: string
  archivedAt?: string
  dataNote?: string
}

/**
 * Danh sách module và trạng thái lưu trữ. Nguồn là
 * `ecdp_backend_source/src/shared/module-registry.ts` — sửa ở đó, không
 * hardcode danh sách ở frontend, tránh hai đầu lệch nhau.
 */
export function useAppModules() {
  return useQuery<AppModule[]>({
    queryKey: ['app-modules'],
    // Chỉ đổi khi deploy lại backend nên không cần hỏi lại liên tục.
    staleTime: 10 * 60_000,
    queryFn: async () => {
      const { data } = await api.get<{ modules: AppModule[] }>('/modules')
      return data.modules
    },
  })
}

/** Module có đang bật không. Chưa tải xong hoặc khoá lạ → coi như BẬT. */
export function useModuleEnabled(key: string): boolean {
  const { data } = useAppModules()
  const m = data?.find((x) => x.key === key)
  return !(m?.archived ?? false)
}

// ── Widget Live Chat cho website ────────────────────────────────────

export interface WebsiteWidget {
  id: string
  name: string
  siteKey: string
  domains: string[]
  isActive: boolean
  /** Tên thương hiệu khách nhìn thấy trong cửa sổ chat (khác `name` — dùng nội bộ). */
  displayName: string | null
  /** Logo hiện trên nút chat và đầu cửa sổ. */
  logoUrl: string | null
  title: string
  greeting: string
  primaryColor: string
  position: string
  liveChatEnabled: boolean
  /** Lời chào lớn ở màn chào (rỗng → câu mặc định theo tên hiển thị). */
  homeTitle: string | null
  /** Kênh liên hệ ở màn chào — merchant tự thêm (xem lib/widget-contacts.ts). */
  contactLinks: ContactLink[]
  /** CŨ — đã chuyển sang contactLinks. */
  zaloUrl: string | null
  facebookUrl: string | null
  phoneNumber: string | null
  conversationCount?: number
}

export function useWidgets() {
  return useQuery<WebsiteWidget[]>({
    queryKey: ['widgets'],
    queryFn: async () => {
      const { data } = await api.get<{ widgets: WebsiteWidget[] }>('/widgets')
      return data.widgets
    },
  })
}

export function useCreateWidget() {
  const qc = useQueryClient()
  return useMutation<WebsiteWidget, unknown, { name: string; domains?: string[] }>({
    mutationFn: async (body) => (await api.post('/widgets', body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['widgets'] }),
  })
}

export function useUpdateWidget() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, ...body }: Partial<WebsiteWidget> & { id: string }) => {
      await api.put(`/widgets/${id}`, body)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['widgets'] }),
  })
}

export function useDeleteWidget() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => { await api.delete(`/widgets/${id}`) },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['widgets'] }),
  })
}

// ─────────────────────────────────────────────────────────────────────────────
// Zalo Bot — kênh dễ đấu nối nhất: khách chỉ dán Bot Token, không OAuth.
// ─────────────────────────────────────────────────────────────────────────────

export interface ZaloBotConfigStatus {
  configured: boolean
  apiBase: string
  publicHttps: boolean
  hasEncryptionKey: boolean
  webhookUrlSample: string
  docsUrl: string
}

export interface ZaloBotConnectResult {
  id: string
  botId: string
  name?: string
  status: string
  webhookUrl: string
  webhookOk: boolean
  message: string | null
}

export function useZaloBotConfigStatus() {
  return useQuery<ZaloBotConfigStatus>({
    queryKey: ['integrations', 'zalo-bot', 'config-status'],
    queryFn: async () => {
      const { data } = await api.get<ZaloBotConfigStatus>('/zalo-bot/config-status')
      return data
    },
  })
}

export function useZaloBotAccounts() {
  return useQuery<ChannelAccount[]>({
    queryKey: [...zaloAccountsKey(undefined, false), 'zalo-bot'],
    queryFn: async () => {
      const { data } = await api.get<ChannelAccount[]>('/zalo-accounts')
      return data.filter((a) => a.platform === PLATFORM.ZALO_BOT)
    },
  })
}

/** Dán Bot Token → backend gọi getMe để xác thực rồi tự đăng ký webhook. */
export function useConnectZaloBot() {
  const qc = useQueryClient()
  return useMutation<ZaloBotConnectResult, unknown, string>({
    mutationFn: async (token) => {
      const { data } = await api.post<ZaloBotConnectResult>('/zalo-bot/connect', { token })
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['integrations', 'zalo-accounts'] }),
  })
}

/** Đăng ký lại webhook — dùng khi đổi tên miền hoặc lần đầu chưa ăn. */
export function useRebindZaloBotWebhook() {
  const qc = useQueryClient()
  return useMutation<{ ok: boolean; webhookUrl: string; hint: string | null }, unknown, string>({
    mutationFn: async (id) => {
      const { data } = await api.post(`/zalo-bot/${id}/rebind-webhook`)
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['integrations', 'zalo-accounts'] }),
  })
}

export interface ZaloBotDiagnosis {
  botId: string | null
  status: string
  isDisabled: boolean
  webhookUrlExpected: string
  webhookUrlTaiZalo: string | null
  khop: boolean
  goiThuOk: boolean | null
  outcome: string | null
  hint: string | null
  infoError: string | null
  soHoiThoai: number
  soTin: number
  tinGanNhat: string | null
}

/** Hỏi thẳng Zalo đang giữ webhook nào + đếm tin đã vào DB. */
export function useDiagnoseZaloBot() {
  return useMutation<ZaloBotDiagnosis, unknown, string>({
    mutationFn: async (id) => {
      const { data } = await api.get<ZaloBotDiagnosis>(`/zalo-bot/${id}/diagnose`)
      return data
    },
  })
}

// ─────────────────────────────────────────────────────────────────────────────
// Telegram — Bot (dán Bot Token) + Telegram Business (tài khoản cá nhân gắn bot)
// ─────────────────────────────────────────────────────────────────────────────

export type TelegramBotConfigStatus = ZaloBotConfigStatus

export interface TelegramBotConnectResult {
  id: string
  botId: string
  username: string | null
  name?: string
  canConnectToBusiness: boolean | null
  status: string
  webhookUrl: string
  webhookOk: boolean
  message: string | null
}

export function useTelegramBotConfigStatus() {
  return useQuery<TelegramBotConfigStatus>({
    queryKey: ['integrations', 'telegram-bot', 'config-status'],
    queryFn: async () => {
      const { data } = await api.get<TelegramBotConfigStatus>('/telegram-bot/config-status')
      return data
    },
  })
}

/** Bot Telegram (platform 12) — lọc từ danh sách tài khoản chung. */
export function useTelegramBotAccounts() {
  return useQuery<ChannelAccount[]>({
    queryKey: [...zaloAccountsKey(undefined, false), 'telegram-bot'],
    queryFn: async () => {
      const { data } = await api.get<ChannelAccount[]>('/zalo-accounts')
      return data.filter((a) => a.platform === PLATFORM.TELEGRAM)
    },
  })
}

/**
 * Tài khoản Telegram cá nhân đã gắn bot qua Telegram Business (platform 13).
 * externalPageId = `tgbiz:<botId>:<connectionId>` — botId cho biết đi qua bot nào.
 */
export function useTelegramBusinessAccounts() {
  return useQuery<ChannelAccount[]>({
    queryKey: [...zaloAccountsKey(undefined, false), 'telegram-business'],
    queryFn: async () => {
      const { data } = await api.get<ChannelAccount[]>('/zalo-accounts')
      return data.filter((a) => a.platform === PLATFORM.TELEGRAM_BUSINESS)
    },
  })
}

/** Bot cha của một kết nối Business, đọc từ externalPageId `tgbiz:<botId>:<connId>`. */
export function telegramBusinessBotId(acc: Pick<ChannelAccount, 'externalPageId'>): string | null {
  const m = acc.externalPageId?.match(/^tgbiz:([^:]+):/)
  return m ? m[1] : null
}

/** Dán Bot Token → backend gọi getMe, sinh secret, tự setWebhook. */
export function useConnectTelegramBot() {
  const qc = useQueryClient()
  return useMutation<TelegramBotConnectResult, unknown, string>({
    mutationFn: async (token) => {
      const { data } = await api.post<TelegramBotConnectResult>('/telegram-bot/connect', { token })
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['integrations', 'zalo-accounts'] }),
  })
}

/** Đăng ký lại webhook (sinh secret mới) — khi đổi tên miền hoặc lần đầu chưa ăn. */
export function useRebindTelegramWebhook() {
  const qc = useQueryClient()
  return useMutation<{ ok: boolean; webhookUrl: string }, unknown, string>({
    mutationFn: async (id) => {
      const { data } = await api.post(`/telegram-bot/${id}/rebind-webhook`)
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['integrations', 'zalo-accounts'] }),
  })
}

/** Ngắt bot: gỡ webhook bên Telegram + gỡ các kết nối Business đi qua bot. */
export function useDisconnectTelegramBot() {
  const qc = useQueryClient()
  return useMutation<void, unknown, string>({
    mutationFn: async (id) => {
      await api.delete(`/telegram-bot/${id}`)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['integrations', 'zalo-accounts'] }),
  })
}

export interface TelegramBotDiagnosis {
  botId: string | null
  status: string
  isDisabled: boolean
  hasSecret: boolean
  webhookUrlExpected: string
  webhookUrlTaiTelegram: string | null
  khop: boolean
  pendingUpdates: number | null
  lastErrorAt: string | null
  lastErrorMessage: string | null
  allowedUpdates: string[] | null
  infoError: string | null
  soHoiThoai: number
  soTin: number
  soKetNoiBusiness: number
  tinGanNhat: string | null
}

/** getWebhookInfo của Telegram + đếm tin đã vào DB. */
export function useDiagnoseTelegramBot() {
  return useMutation<TelegramBotDiagnosis, unknown, string>({
    mutationFn: async (id) => {
      const { data } = await api.get<TelegramBotDiagnosis>(`/telegram-bot/${id}/diagnose`)
      return data
    },
  })
}

// ─────────────────────────────────────────────────────────────────────────────
// Facebook Page — chẩn đoán "page kết nối rồi mà không có tin"
// ─────────────────────────────────────────────────────────────────────────────

export interface FbDiagnosis {
  pageId: string
  pageName: string | null
  status: string
  isDisabled: boolean
  appDaKhaiWebhook: boolean
  appCoTruongMessages: boolean
  appCallbackUrl: string | null
  appFields: string[]
  appError: string | null
  pageDaDangKyApp: boolean
  pageCoTruongMessages: boolean
  pageFields: string[]
  appKhacDangNhan: string[]
  pageError: string | null
  luotGoiGanDay: Array<{ at: string; signatureOk: boolean; pageIds: string[]; soSuKien: number }>
  soHoiThoai: number
  soTin: number
  tinGanNhat: string | null
}

export function useDiagnoseFacebookPage() {
  return useMutation<FbDiagnosis, unknown, string>({
    mutationFn: async (id) => {
      const { data } = await api.get<FbDiagnosis>(`/facebook-page/${id}/diagnose`)
      return data
    },
  })
}

// ─────────────────────────────────────────────────────────────────────────────
// Hạn mức Zalo cá nhân — xem backend zalo-policy.ts để biết nguồn từng con số
// ─────────────────────────────────────────────────────────────────────────────

export type ZaloTier = 'free' | 'lite' | 'pro' | 'max'

export interface ZaloRateStatus {
  tier: ZaloTier
  tierLabel: string
  categories: Record<string, { used: number; dailyLimit: number | null; label: string }>
  strangers: { used: number; quota: number | null; friendsSynced: boolean }
  contacts: { count: number; cap: number }
}

export const ZALO_TIER_OPTIONS: { value: ZaloTier; label: string }[] = [
  { value: 'free', get label() { return i18n.t('chanCatalog:zaloTier.free') } },
  { value: 'lite', label: 'zBusiness Lite' },
  { value: 'pro', label: 'zBusiness Pro' },
  { value: 'max', label: 'zBusiness Max' },
]

export function useZaloRateStatus(accountId: string) {
  return useQuery<ZaloRateStatus>({
    queryKey: ['integrations', 'zalo-rate', accountId],
    queryFn: async () => {
      const { data } = await api.get<ZaloRateStatus>(`/zalo-accounts/${accountId}/rate-status`)
      return data
    },
    // Số đếm nhích lên theo từng tin gửi đi — làm mới vừa phải, không cần realtime.
    refetchInterval: 60_000,
  })
}

export function useSetZaloTier() {
  const qc = useQueryClient()
  return useMutation<ZaloRateStatus, unknown, { accountId: string; tier: ZaloTier }>({
    mutationFn: async ({ accountId, tier }) => {
      const { data } = await api.put<ZaloRateStatus>(`/zalo-accounts/${accountId}/tier`, { tier })
      return data
    },
    onSuccess: (data, { accountId }) => qc.setQueryData(['integrations', 'zalo-rate', accountId], data),
  })
}

// ─────────────────────────────────────────────────────────────────────────────
// Proxy riêng từng tài khoản Zalo cá nhân
// ─────────────────────────────────────────────────────────────────────────────

export interface ZaloProxyStatus {
  configured: boolean
  /** Đã che mật khẩu, dạng http://***@host:port */
  proxy: string | null
  error?: string
}

const proxyKey = (accountId: string) => ['integrations', 'zalo-proxy', accountId]

export function useZaloProxy(accountId: string) {
  return useQuery<ZaloProxyStatus>({
    queryKey: proxyKey(accountId),
    queryFn: async () => (await api.get<ZaloProxyStatus>(`/zalo-accounts/${accountId}/proxy`)).data,
  })
}

/** Thử proxy (bỏ trống = thử proxy đang lưu), trả IP đi ra. */
export function useTestZaloProxy() {
  return useMutation<{ ok: boolean; ip: string; latencyMs: number; proxy: string }, unknown, { accountId: string; proxy?: string }>({
    mutationFn: async ({ accountId, proxy }) =>
      (await api.post(`/zalo-accounts/${accountId}/proxy/test`, { proxy })).data,
  })
}

export function useSaveZaloProxy() {
  const qc = useQueryClient()
  return useMutation<ZaloProxyStatus & { ip: string; reconnecting: boolean }, unknown, { accountId: string; proxy: string }>({
    mutationFn: async ({ accountId, proxy }) =>
      (await api.put(`/zalo-accounts/${accountId}/proxy`, { proxy })).data,
    onSuccess: (_d, { accountId }) => {
      qc.invalidateQueries({ queryKey: proxyKey(accountId) })
      qc.invalidateQueries({ queryKey: ['integrations', 'zalo-accounts'] })
    },
  })
}

export function useRemoveZaloProxy() {
  const qc = useQueryClient()
  return useMutation<ZaloProxyStatus & { reconnecting: boolean }, unknown, string>({
    mutationFn: async (accountId) => (await api.delete(`/zalo-accounts/${accountId}/proxy`)).data,
    onSuccess: (_d, accountId) => {
      qc.invalidateQueries({ queryKey: proxyKey(accountId) })
      qc.invalidateQueries({ queryKey: ['integrations', 'zalo-accounts'] })
    },
  })
}

export interface ZaloProxyHistory {
  since: string | null
  checks: number
  failures: number
  uptimePct: number | null
  currentIp: string | null
  distinctIps: number
  ipChanges: number
  lastChangeAt: string | null
  connects: number
  closes: { total: number; elsewhere: number; network: number; other: number; lastAt: string | null; lastCode: number | null }
  changes: Array<{ at: string; from: string; to: string }>
  recent: Array<{ at: string; kind: string; ok: boolean; ip: string | null; latencyMs: number | null; poolStatus: string | null; error: string | null }>
}

/** Lịch sử IP đi ra qua proxy — hệ thống tự kiểm tra mỗi 30 phút. */
export function useZaloProxyHistory(accountId: string, enabled = true) {
  return useQuery<ZaloProxyHistory>({
    queryKey: ['integrations', 'zalo-proxy-history', accountId],
    queryFn: async () => (await api.get<ZaloProxyHistory>(`/zalo-accounts/${accountId}/proxy/history?days=14`)).data,
    enabled,
    refetchInterval: 5 * 60_000,
  })
}
